import { Button, Card, CardBody, Checkbox, DataTable, Input, Switch, Tabs, useToast } from "@aidkitorg/component-library";
import { Survey, TargetFieldRef } from "@aidkitorg/types/lib/survey";
import { Dependency, DependencyMap, DistroComponent, TRANSLATORS } from "@aidkitorg/types/lib/translation/questions";
import { Menu } from "@headlessui/react";
import { Position } from "cytoscape";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import { findAndGoToComponent } from "./ConfigSearch";
import { useDebouncedCallback } from "./Hooks/Debounce";
import { InfoDict } from "./Questions/Props";
import { stringToColor } from "./Util";
import { buildTargetFieldPathMap, getDependencyMap } from "./utils/dependencyMap";

const EDGE_COLOR_MAP: Record<Dependency['type'], string> = {
  formula: '#a9b7e7',
  content: '#fee685',
  conditional: '#ffa2a2',
  // Unused for the moment.
  // notif: 'green',
  // payment: 'magenta',
}

/**
* Takes in a DependencyMap, a initial TargetField, and a dependency direction and assembles an object with an array
* of Cytoscape nodes and an array of Cytoscape edges.
* If one wants to construct a graph of all the fields startId depends on, they would use 'ancestors', and if they
* wanted a graph of all the fields which depend on startId, they would use 'descendants'
*
* @param map - a map from TargetField to DependencyNode, essentially another TargetField and some additional data. See: DependencyMap in questions.ts
* @param startId - TargetField from which to begin the traversal
* @param direction - 'ancestors' to find fields that startId depends on, 'descendants' to find fields that depend on startId
* @returns nodes: N[] and edges: E[], where N and E are Cytoscape nodes and edges. See: https://js.cytoscape.org/#notation/elements-json
*/
function getAllRelationsByDirection({ map, startId, direction, includedEdgeTypes, ignoredFields }: {
  map: DependencyMap,
  startId: TargetFieldRef,
  direction: 'ancestors' | 'descendants',
  includedEdgeTypes: Record<Dependency['type'], boolean>,
  ignoredFields: Record<TargetFieldRef, boolean>,
}) {
  // Nodes/fields we've seen already
  const visited = new Set<TargetFieldRef>();
  // Edges as a string key "source>>destination>>type" to ensure uniqueness
  const edgeStrings = new Set<string>();
  const warnings: string[] = [];

  // Stack for depth-first-search
  const stack = [startId];

  while (stack.length > 0) {
    const currentId = stack.pop()!;
    // Skip if we’ve already visited
    if (visited.has(currentId)) {
      continue;
    }
    visited.add(currentId);

    const currentNode = map.get(currentId);
    if (!currentNode) {
      continue;
    }

    // This is the key bit that determines if we're looking up or down the dependency graph
    const relations = direction === 'ancestors' ? currentNode.parents : currentNode.children;

    // Add all not-yet-seen edges to edgeStrings, and not-yet-visited nodes/fields to the stack
    for (const relation of relations) {
      if (!includedEdgeTypes[relation.type]) continue;

      // This likely indicates a misconfiguration in Distro
      if (!relation.field) {
        const errorMsg = `Empty reference of type "${relation.type}" found for ${currentId}${currentNode.component ? ' kind: "' + currentNode.component.kind + '"' : ''}`;
        warnings.push(errorMsg);
        console.warn(errorMsg);
        continue;
      }

      if (ignoredFields[relation.field]) {
        continue;
      }

      const edgeKey = direction === 'ancestors'
        ? `${relation.field}>>${currentId}>>${relation.type}`
        : `${currentId}>>${relation.field}>>${relation.type}`;

      if (!edgeStrings.has(edgeKey)) {
        edgeStrings.add(edgeKey);
      }

      // Continue traversal if we haven’t seen the relation yet
      if (!visited.has(relation.field)) {
        stack.push(relation.field);
      }
    }
  }

  return {
    nodes: Array.from(visited).map((node) => {
      const nodeData = map.get(node);
      const colors = node === startId
        ? ['#05df72', '#00ca5f'] // Unique color assigned to highlight the field being focused on.
        : (nodeData?.component?.kind && nodeData!.component.kind !== '(Derived)') // This let's us pick the color for derived fields
          ? [stringToColor(nodeData!.component.kind), stringToColor(nodeData!.component.kind, '85%')]
          : direction === 'ancestors'
            ? ['#f2f4fc', '#ccd3f1'] // --ak-color-brand-blue-50
            : ['#fcfccc', '#f4e897']; // light yellow
      return {
        data: {
          id: node,
          kind: nodeData?.component?.kind,
          color: colors[0],
          border: colors[1],
        }
      }
    }),
    edges: Array.from(edgeStrings).map((key) => {
      const [source, target, type] = key.split('>>');
      return {
        data: {
          source,
          target,
          color: EDGE_COLOR_MAP[type as Dependency['type']]
        }
      }
    }),
    warnings
  };
}

const MENU_ACTIONS = {
  IGNORE_NODE: 'Ignore this node',
  GRAPH_NODE: 'Graph this node'
} as const;

type MenuActions = keyof typeof MENU_ACTIONS;

export function TargetFieldExplorer(props: {
  survey: Survey,
  mockInfo: InfoDict,
  setMockInfo: (value: React.SetStateAction<InfoDict>) => void
}) {
  const cyRef = useRef<cytoscape.Core | null>(null);

  const [selectedTab, setSelectedTab] = useState<'Field table' | 'Graph'>('Field table');
  const [showFieldsWithoutValues, setShowFieldsWithoutValues] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNode, setSelectedNode] = useState<cytoscape.NodeSingular | null>(null);
  const [showControls, setShowControls] = useState(false);
  const [includedEdgeTypes, setIncludedEdgeTypes] = useState<Record<Dependency['type'], boolean>>({
    conditional: true,
    content: true,
    formula: true,
  });
  const [ignoredFields, setIgnoredFields] = useState<Record<TargetFieldRef, boolean>>({});
  const { toast } = useToast();
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<Position>({ x: 0, y: 0 });

  const targetFieldPathMap = useMemo(
    () => buildTargetFieldPathMap(props.survey),
    [props.survey]
  );

  // This is pretty fast, ~ 15-50ms, but no need to recompute it
  // unless props.survey changes
  const dependencyMap = useMemo(
    () => getDependencyMap(props.survey),
    [props.survey]
  );

  const sortedFields = useMemo(
    () => Array.from(dependencyMap.keys()).sort().map(field => ({ field, value: props.mockInfo[field] })),
    [dependencyMap, props.mockInfo]
  );

  const filteredFields = searchTerm
    ? sortedFields.filter(field => field.field.toLowerCase().includes(searchTerm.toLowerCase()))
    : sortedFields;

  // Build the Cytoscape graph from the dependency map, centered around searchTerm
  const graphInfo = useMemo(() => {
    if (!searchTerm || !sortedFields.some(field => field.field === searchTerm)) return [];

    const ancestors = getAllRelationsByDirection({
      map: dependencyMap,
      startId: searchTerm,
      direction: 'ancestors',
      includedEdgeTypes,
      ignoredFields,
    });
    const descendants = getAllRelationsByDirection({
      map: dependencyMap,
      startId: searchTerm,
      direction: 'descendants',
      includedEdgeTypes,
      ignoredFields
    });

    [...ancestors.warnings, ...descendants.warnings].forEach(warning => {
      toast({
        description: warning,
        variant: 'error',
      });
    })

    return [
      ...ancestors.nodes,
      ...descendants.nodes,
      ...ancestors.edges,
      ...descendants.edges,
    ];
  }, [dependencyMap, searchTerm, sortedFields, ignoredFields, includedEdgeTypes, toast]);

  // Memoizing this and the stylesheet below allow us to avoid sending them as props to the graph
  // component, which avoids it rerendering on every render of TargetFieldExplorer
  const graphLayout = useMemo(() => ({
    name: 'elk',
    fit: true,
    padding: 0,
    elk: {
      algorithm: 'layered',
      'elk.direction': 'DOWN',
      'elk.layered.spacing.edgeEdgeBetweenLayers': 30,
      'elk.layered.spacing.edgeNodeBetweenLayers': 20,
      'elk.spacing.nodeNode': 20,
      'elk.partitioning.activate': true,
    },
  }), []);

  const graphStylesheet = useMemo(() => ([
    {
      selector: 'node',
      style: {
        shape: 'round-rectangle',
        width: (node: any) => Math.max(node.data('kind')?.length ?? 0, node.data('id').length) * 9,
        height: 50,
        'background-opacity': '0.8',
        'background-color': 'data(color)',
        label: (node: any) => (node.data('kind') ? node.data('kind') + '\n' : '') + node.data('id'),
        'text-wrap': 'wrap',
        'line-height': 1.5,
        'text-valign': 'center',
        'border-width': 1,
        'border-style': 'solid',
        'border-color': 'data(border)',
      },
    },
    {
      selector: 'node.selected',
      style: {
        'border-width': 4,
        'border-color': '#F59E0B', // amber-500
        'background-color': '#00d492', // optional highlight
      },
    },
    {
      selector: 'edge',
      style: {
        'line-color': 'data(color)',
        'mid-target-arrow-color': 'data(color)',
        'mid-target-arrow-shape': 'triangle',
        'target-distance-from-node': 3,
        'arrow-scale': 2,
        'curve-style': 'bezier',
      },
    }
  ]), []);

  // We redo the whole graph if a matching searchTerm is found in the map,
  // so we debounce to avoid destroying and recreating it over and over.
  const handleNewFieldSelection = useDebouncedCallback(
    (field: string) => {
      setSearchTerm(field);
      setShowContextMenu(false);
    },
    300,
    [] // Only once
  );

  // Set up listeners, and remove them if they already exist.
  const handleCyInit = useCallback((cy: cytoscape.Core) => {
    cyRef.current = cy;

    // Remove previous handlers if they exist
    cy.off('cxttap', 'node');
    cy.off('onetap', 'node');
    cy.off('onetap');

    cy.on('cxttap', 'node', (evt: cytoscape.EventObject) => {
      setShowContextMenu(true);
      setSelectedNode(evt.target);

      // Set position to bottom left, i.e. directly under the node
      const box = evt.target.renderedBoundingBox();
      setContextMenuPosition({ x: box.x1, y: box.y2 });

      cy.nodes().removeClass('selected'); // Remove previous selection
      evt.target.addClass('selected');  // Set this node as selected
    });

    cy.on('onetap', 'node', handleNodeClick);

    cy.on('onetap', (evt: cytoscape.EventObject) => {
      if (evt.target === cy) {
        setShowContextMenu(false);
        cy.nodes().removeClass('selected'); // Remove previous selection
      }
    });
  }, []);

  // Scrolls to the component in Distro using ConfigSearch.tsx
  const handleNodeClick = useCallback((evt: cytoscape.EventObject) => {
    const node = evt.target;
    const nodeData = node.data();
    const tree = targetFieldPathMap[nodeData.id];
    if (tree) {
      findAndGoToComponent(tree, props.survey);
    }
  }, [targetFieldPathMap, props.survey]);

  // Remove all listeners on component dismount
  useEffect(() => {
    return () => {
      cyRef.current?.removeAllListeners();
    }
  }, []);

  // useLayoutEffect is needed for the graph component to be interactable without needing an additional render.
  // Manually update the elements of the graph to avoid triggering a re-render/re-layout
  useLayoutEffect(() => {
    const cy = cyRef.current;
    if (!cy || selectedTab !== 'Graph') return;


    // Update the Cytoscape graph's nodes/edges, then re-layout the graph
    cy.elements().remove();
    cy.add(graphInfo);
    cy.layout(graphLayout).run();

    // This timeout ensures the graph runs its layout after its parent is painted
    const timeout = setTimeout(() => {
      cy.resize();
      cy.fit();
    }, 50);

    return () => clearTimeout(timeout);
  }, [graphInfo, graphLayout, selectedTab])

  // This effect keeps the positioning of the context menu in sync with the node its attached to
  // when the user pans, zooms, or drags the node
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;

    const handleMove = () => {
      if (selectedNode) {
        const box = selectedNode.renderedBoundingBox();
        setContextMenuPosition({ x: box.x1, y: box.y2 });
      }
    };

    // These events come from here: https://js.cytoscape.org/#events
    // and are used to ensure the context menu follows the node on pan, zoom, and drag
    cy.on('pan zoom drag', handleMove);

    return () => {
      cy.off('pan zoom drag', handleMove);
    };
  }, [selectedNode]);

  const handleContextMenuAction = (action: MenuActions) => {
    // Start by closing the menu
    setShowContextMenu(false);

    if (!selectedNode) return;

    switch(action) {
      case 'IGNORE_NODE':
        setIgnoredFields(prev => ({ ...prev, [selectedNode.id()]: true }));
        break;
      case 'GRAPH_NODE':
        setInputValue(selectedNode.id());
        handleNewFieldSelection(selectedNode.id()); // ID here is the node's targetField
        setIgnoredFields({}); // Unhide all nodes
        break;
    }
  }

  return <div className='mx-3 h-full'>
    <div className="flex w-full gap-2 pb-2 items-center">
      <div className="grow">
        {/* TODO: This should probably be a Combobox, but currently that component can't handle potentially thousands of fields */}
        <Input
          type="text"
          label={'Search for a Field...'}
          showLabel={false}
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value);
            handleNewFieldSelection(event.target.value);
          }}
          placeholder={'Search for a Field...'}
          className="mt-2 p-2 h-full border border-gray-300 rounded-md text-sm w-full"
        />
      </div>
    </div>
    <Tabs
      onChange={(key) => setSelectedTab(key as 'Field table' | 'Graph')}
      tabs={[
        {
          key: 'Field table',
          name: 'Field table',
          content: <div className='h-full'>
            <div className='flex justify-between mt-2 ml-2'>
              <Switch
                label="Show Fields without Values"
                checked={showFieldsWithoutValues}
                onCheckedChange={() => setShowFieldsWithoutValues(prev => !prev)}
              />
              <Button onClick={() => { props.setMockInfo({}) }} color="blue">Clear Field Values</Button>
            </div>
            <DataTable
              columns={[{ accessorKey: 'field', header: 'Field Name' }, { accessorKey: 'value', header: 'Value' }]}
              data={showFieldsWithoutValues ? filteredFields : filteredFields.filter(field => !!field.value)}
              searchLabel="Search for a field"
              compact={true}
              pageSize={15}
            />
          </div>
        },
        {
          key: 'Graph',
          name: 'Graph',
          content: <div className='relative w-full h-[70vh]'>
            <div className='absolute t-1 r-1 z-10 p-2'>
              <Switch
                label="Show Controls"
                onCheckedChange={() => setShowControls(prev => !prev)}
                className='my-2'
              />
              <div hidden={!showControls}>
                <Card className='p-4 shadow-md'>
                  <CardBody>
                    <label className='font-bold'>Dependency Type</label>
                    {(Object.keys(EDGE_COLOR_MAP) as (keyof typeof EDGE_COLOR_MAP)[]).map((edgeType) => {
                      return (
                        <div key={edgeType} className='flex items-center mb-2'>
                          <span className='w-3 h-3 mr-2' style={{ background: EDGE_COLOR_MAP[edgeType] }}></span>
                          <Checkbox
                            label={edgeType}
                            checked={includedEdgeTypes[edgeType]}
                            onChange={(checked) => { setIncludedEdgeTypes(prev => ({ ...prev, [edgeType]: checked })) }}
                          />
                        </div>
                      )
                    })}
                    <label className='font-bold mt-2 mb-0'>Ignored Fields</label>
                    <div className='max-h-[250px] max-w-[250px] overflow-auto mb-2'>
                      {(Object.keys(ignoredFields).map(ignoredField => {
                        return ignoredFields[ignoredField]
                          ? <div className='font-light hover:font-medium cursor-pointer' onClick={() => setIgnoredFields(prev => ({ ...prev, [ignoredField]: false }))}>
                            {ignoredField}
                          </div>
                          : null
                      }))}
                    </div>
                    <Button onClick={() => setIgnoredFields({})}>Reset ignored fields</Button>
                  </CardBody>
                </Card>
              </div>
            </div>
      
            {/* Right-click Context Menu */}
            {showContextMenu && (
              <div
                className="absolute z-50 bg-white border shadow-lg rounded-md"
                style={{ top: contextMenuPosition.y, left: contextMenuPosition.x }}
              >
                <Menu as="div" className="flex flex-col p-2">
                  {(Object.keys(MENU_ACTIONS) as MenuActions[]).map((action) => (
                    <Menu.Item key={action}>
                      {({ active }) => (
                        <button
                          className={`px-4 py-2 ${active ? 'bg-[var(--ak-color-brand-blue-800)] text-white outline-none' : ''}`}
                          onClick={() => handleContextMenuAction(action)}
                        >
                          {MENU_ACTIONS[action]}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </Menu>
              </div>
            )}
      
            {/* The Dependency Graph */}
            <div className="h-full w-full p-2 relative">
              <CytoscapeComponent
                cy={handleCyInit}
                elements={[]} // Set manually in handleCyInit()
                stylesheet={graphStylesheet}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </div>
        }
      ]}
    />
  </div>
}